import { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { ErrorScreen, Header, Icon } from '@paysense-india-services/elevate-web'

import { ROUTES } from '@/constants/routes'

import CardComponent from '@/components/CardComponent'

import {
  findRepaymentItem,
  getRepaymentDetailsSection
} from '@/app/pages/repaymentDetailsPage/utils'

import styles from './index.module.scss'

const STATUS_COLOR_MAP = {
  'content/warning': '#F59E0B',
  'content/success': '#10B981',
  'content/error': '#EF4444'
}

const getStatusColor = color => {
  return STATUS_COLOR_MAP[color] || color || '#10B981'
}

const formatPaymentMode = mode => {
  if (!mode) return '—'
  const modeMap = {
    nach: 'NACH',
    debitcard: 'Debit card',
    creditcard: 'Credit card',
    upi: 'UPI',
    netbanking: 'Net banking'
  }
  return modeMap[mode.toLowerCase()] || mode
}

const RepaymentDetailsPage = ({ historyData }) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const loanId = searchParams.get('loanId') ?? searchParams.get('loan_id')
  const txnIndex = searchParams.get('txnIndex')

  const normalizedHistory = useMemo(() => historyData?.screen ?? historyData, [historyData])

  const repaymentData = useMemo(
    () => findRepaymentItem(normalizedHistory, { loanId, txnIndex }),
    [normalizedHistory, loanId, txnIndex]
  )

  const handleCopy = text => {
    navigator.clipboard.writeText(text)
    showSnackbar('Copied to clipboard', { type: 'success' })
  }

  if (!repaymentData) {
    return (
      <>
        <Header
          className='ew-bg-secondary ew-shadow-none'
          isBackButton={true}
          onBack={() => navigate(ROUTES.HISTORY)}
          title='Transaction details'
        />
        <div className={styles.container}>
          <ErrorScreen
            buttonText='Back to history'
            description='We could not find this transaction. Please go back and try again.'
            onButtonClick={() => navigate(ROUTES.HISTORY)}
            title='Transaction not found'
          />
        </div>
      </>
    )
  }

  const dependentSections = normalizedHistory?.dependentSections?.items || []

  return (
    <>
      <Header
        className='ew-bg-secondary ew-shadow-none ew-border-bottom'
        isBackButton={true}
        onBack={() => navigate(-1)}
        style={{ top: 0 }}
        title='Transaction details'
      />
      <div className={`${styles.container} ew-overflow-y-auto ew-pb-16`}>
        <div className='ew-px-16 ew-mt-16'>
          <CardComponent>
            <div className='ew-d-flex ew-gap-10 ew-pb-12 ew-border-bottom'>
              {repaymentData?.assets?.leadingIcon ? (
                <div
                  className={`${styles.iconContainer} ew-border ew-rounded-md ew-p-8 ew-d-flex ew-align-items-center ew-justify-content-center`}>
                  <img alt='' height={24} src={repaymentData.assets.leadingIcon} width={24} />
                </div>
              ) : null}

              <div className='ew-d-flex ew-flex-column ew-flex-grow-1'>
                <span className='ew-typography-normal ew-fw-semi-bold ew-color-primary'>
                  {repaymentData?.title}
                </span>
                <span className='ew-typography-small ew-fw-regular ew-color-secondary'>
                  {repaymentData?.description}
                </span>
              </div>

              <div className='ew-d-flex ew-flex-column ew-align-items-end'>
                <span className='ew-typography-normal ew-fw-semi-bold ew-color-primary'>
                  {repaymentData?.subtitle}
                </span>
                {repaymentData?.meta?.label ? (
                  <span
                    className='ew-typography-small ew-fw-semi-bold'
                    style={{ color: getStatusColor(repaymentData.meta.color) }}>
                    {repaymentData.meta.label}
                  </span>
                ) : null}
              </div>
            </div>

            <div className='ew-d-flex ew-flex-column ew-gap-12 ew-pt-12'>
              {repaymentData?.meta?.transaction_id ? (
                <div className='ew-d-flex ew-justify-content-between ew-align-items-center'>
                  <span className='ew-typography-small ew-fw-regular ew-color-secondary'>
                    Transaction ID
                  </span>
                  <button
                    className={`${styles.copyButton} ew-d-flex ew-align-items-center ew-gap-4`}
                    onClick={() => handleCopy(String(repaymentData.meta.transaction_id))}
                    type='button'>
                    <Icon name='copy' size={16} />
                    <span className='ew-typography-small ew-fw-semi-bold ew-color-primary'>
                      {repaymentData.meta.transaction_id}
                    </span>
                  </button>
                </div>
              ) : null}

              {repaymentData?.meta?.mode ? (
                <div className='ew-d-flex ew-justify-content-between ew-align-items-center'>
                  <span className='ew-typography-small ew-fw-regular ew-color-secondary'>
                    Payment mode
                  </span>
                  <span className='ew-typography-small ew-fw-semi-bold ew-color-primary'>
                    {formatPaymentMode(repaymentData.meta.mode)}
                  </span>
                </div>
              ) : null}
            </div>
          </CardComponent>
        </div>

        {dependentSections.length > 0 ? (
          <div className='ew-px-16 ew-mt-16 ew-d-flex ew-flex-column ew-gap-16'>
            {dependentSections.map((section, index) => getRepaymentDetailsSection(section, index))}
          </div>
        ) : null}
      </div>
    </>
  )
}

export default RepaymentDetailsPage
